/**
 * discBboxGuard.js — Phase D · Step 2 · centralized bbox-render guard
 *
 * Single source of truth for the "should I render this bbox overlay?"
 * decision. Replaces ad-hoc `annotation?.bbox_xyxy && !draftBox` checks
 * scattered through the triage canvas so future annotation statuses
 * (e.g. `confirmed_partial`, `occluded`, `low_confidence`) inherit the
 * defensive contract without re-discovering the same null-pointer
 * crash that hit `confirmed_empty` rows on first ship.
 *
 * Contract — all four must hold for the render closure to fire:
 *   1. `annotation` is truthy.
 *   2. `draftBox` is falsy (no in-progress click-drag draw).
 *   3. `annotation.bbox_xyxy` is an Array.
 *   4. The array has exactly 4 finite numeric elements.
 *
 * Why exactly 4 numeric? Because the underlying CSS-percentage helper
 * (`sourceToRenderedPct`) destructures `{x1, y1, x2, y2}` — anything
 * else (3-element array, NaN coords, string "null") would silently
 * paint an invisible / oversized / off-canvas overlay. Better to skip
 * the render entirely and surface the issue via a console.warn() so
 * it shows up in the operator log.
 */
export function isValidBbox(bbox) {
  if (!Array.isArray(bbox)) return false;
  if (bbox.length !== 4) return false;
  for (let i = 0; i < 4; i++) {
    const v = bbox[i];
    if (typeof v !== "number" || !Number.isFinite(v)) return false;
  }
  return true;
}

/**
 * Wrapper for JSX render paths. If the contract holds, invokes
 * `renderFn(bbox_xyxy)` and returns whatever it returns (the JSX
 * fragment). If not, returns `null` so React renders nothing.
 *
 * The signature deliberately mirrors a normal React render expression
 * — drop it inline like `{renderBboxIfValid(ann, draft, (b) => <Box… />)}`
 * with zero ceremony.
 */
export function renderBboxIfValid(annotation, draftBox, renderFn) {
  if (!annotation) return null;
  if (draftBox) return null;
  if (!isValidBbox(annotation.bbox_xyxy)) {
    // Only warn for the SUSPICIOUS shape — null + missing are LEGAL for
    // confirmed_empty rows and shouldn't spam the console.
    if (annotation.bbox_xyxy != null) {
      // eslint-disable-next-line no-console
      console.warn(
        "[disc-bbox-guard] skipping render: malformed bbox_xyxy",
        { status: annotation.status, bbox: annotation.bbox_xyxy },
      );
    }
    return null;
  }
  return renderFn(annotation.bbox_xyxy);
}
